import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertCircle,
  Database,
  Target,
  Building2,
  TrendingUp,
  ChevronRight,
  Info,
  Sparkles,
  ExternalLink,
  BarChart3,
  DollarSign,
  Clock,
  Save,
  Trash2,
  RefreshCw,
  FileText,
  Bookmark,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { useApp } from '../context/AppContext';
import { 
  getExcelTemplate, 
  getAdvancedTemplate, 
  importFromExcel, 
  importAdvancedData,
  getImportTemplates,
  createImportTemplate,
  deleteImportTemplate,
  downloadTemplateExcel,
  getImportHistory,
  rerunImport,
  deleteImportHistory
} from '../lib/api';
import { downloadBlob, formatCurrency, getCurrencySymbol } from '../lib/utils';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { MetricHeroStats } from '../components/layout/MetricHeroStats';
import { getLightSectionTheme } from '../lib/sectionThemes';

export const ImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const advancedFileInputRef = useRef(null);
  const { setConfiguration, loadScenario, currency } = useApp();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [activeTab, setActiveTab] = useState('advanced');
  const [advancedImportData, setAdvancedImportData] = useState(null);
  const [createdScenario, setCreatedScenario] = useState(null);
  
  // History & Templates state
  const [importHistory, setImportHistory] = useState([]);
  const [importTemplates, setImportTemplates] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isRerunning, setIsRerunning] = useState(null);

  // Load history and templates on mount
  useEffect(() => {
    loadImportHistory();
    loadImportTemplates();
  }, []);

  const loadImportHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await getImportHistory();
      setImportHistory(response.data || []);
    } catch (error) {
      logger.error('Error loading import history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadImportTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await getImportTemplates();
      setImportTemplates(response.data || []);
    } catch (error) {
      logger.error('Error loading templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!advancedImportData || !templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      await createImportTemplate({
        name: templateName.trim(),
        description: templateDescription.trim() || null,
        organization: advancedImportData.organization,
        current_lei_values: advancedImportData.current_lei_values,
        target_lei_values: advancedImportData.target_lei_values,
        selected_kpis: advancedImportData.selected_kpis
      });
      toast.success('Template saved successfully');
      setShowSaveTemplateDialog(false);
      setTemplateName('');
      setTemplateDescription('');
      loadImportTemplates();
    } catch (error) {
      logger.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await deleteImportTemplate(templateId);
      toast.success('Template deleted');
      loadImportTemplates();
    } catch (error) {
      logger.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDownloadTemplateFile = async (templateId, templateName) => {
    try {
      const response = await downloadTemplateExcel(templateId);
      downloadBlob(response.data, `${templateName.replace(/\s+/g, '_')}_template.xlsx`);
      toast.success('Pre-filled template downloaded');
      loadImportTemplates(); // Refresh to update use count
    } catch (error) {
      logger.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleRerunImport = async (historyId) => {
    setIsRerunning(historyId);
    try {
      const response = await rerunImport(historyId);
      const result = response.data;
      
      if (result.scenario) {
        setCreatedScenario(result.scenario);
        toast.success('Analysis re-run successfully! New scenario created.');
        loadImportHistory();
      }
    } catch (error) {
      logger.error('Error re-running import:', error);
      toast.error('Failed to re-run import');
    } finally {
      setIsRerunning(null);
    }
  };

  const handleDeleteHistoryEntry = async (historyId) => {
    if (!window.confirm('Delete this history entry?')) return;
    try {
      await deleteImportHistory(historyId);
      toast.success('History entry deleted');
      loadImportHistory();
    } catch (error) {
      logger.error('Error deleting history:', error);
      toast.error('Failed to delete history entry');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await getExcelTemplate();
      downloadBlob(response.data, 'lei_analysis_template.xlsx');
      toast.success('Template downloaded');
    } catch (error) {
      logger.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleDownloadAdvancedTemplate = async () => {
    try {
      const response = await getAdvancedTemplate();
      downloadBlob(response.data, 'lei_advanced_import_template.xlsx');
      toast.success('Advanced template downloaded');
    } catch (error) {
      logger.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const response = await importFromExcel(file);
      const importedConfig = response.data;
      
      setConfiguration(importedConfig);
      setUploadResult({
        success: true,
        message: 'Configuration imported successfully',
        data: importedConfig,
        type: 'simple'
      });
      toast.success('Configuration imported successfully');
    } catch (error) {
      logger.error('Import error:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to import file',
      });
      toast.error('Failed to import file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAdvancedFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadResult(null);
    setAdvancedImportData(null);
    setCreatedScenario(null);

    try {
      const response = await importAdvancedData(file);
      const importedData = response.data;
      
      setAdvancedImportData(importedData);
      
      // Check if a scenario was created
      if (importedData.scenario) {
        setCreatedScenario(importedData.scenario);
        setUploadResult({
          success: true,
          message: 'Data imported and analysis completed! A new scenario has been created.',
          data: importedData,
          type: 'advanced'
        });
        toast.success('Import successful! Scenario created with full analysis results.');
      } else {
        setUploadResult({
          success: true,
          message: 'Advanced data imported successfully',
          data: importedData,
          type: 'advanced'
        });
        toast.success('Data imported successfully! Review the results below.');
      }
      // Refresh import history
      loadImportHistory();
    } catch (error) {
      logger.error('Import error:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to import file',
      });
      toast.error('Failed to import file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, isAdvanced = false) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.xlsx')) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      const inputRef = isAdvanced ? advancedFileInputRef : fileInputRef;
      const handler = isAdvanced ? handleAdvancedFileSelect : handleFileSelect;
      if (inputRef.current) {
        inputRef.current.files = dataTransfer.files;
        handler({ target: { files: dataTransfer.files } });
      }
    } else {
      toast.error('Please upload an Excel file (.xlsx)');
    }
  };

  const proceedToAnalysis = () => {
    if (createdScenario) {
      // If a scenario was created, navigate to view it
      navigate(`/scenarios/${createdScenario.id}`);
    } else if (advancedImportData) {
      // Store the advanced data in context/localStorage for use in analysis
      localStorage.setItem('advancedImportData', JSON.stringify(advancedImportData));
      navigate('/matrix');
    } else {
      navigate('/configure');
    }
  };

  const viewCreatedScenario = () => {
    if (createdScenario) {
      // Navigate directly to the matrix page to view results
      navigate('/matrix');
    }
  };

  const __pageTheme = getLightSectionTheme('Navy');

  // Hero quick-stats — pulled from already-loaded state so no extra API calls.
  const sortedHistory = [...importHistory].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
  const mostRecentImport = sortedHistory[0] || null;
  const fmtRelative = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  const heroStats = (
    <MetricHeroStats
      testId="model-setup-hero-stats"
      tiles={[
        {
          key: 'imports',
          label: 'Total Imports',
          value: importHistory.length,
          hint: importHistory.length > 0 ? 'Lifetime uploads' : 'No imports yet',
          headline: true,
          testId: 'model-setup-stat-imports',
        },
        {
          key: 'last',
          label: 'Last Import',
          value: mostRecentImport
            ? new Date(mostRecentImport.created_at).toLocaleDateString()
            : '—',
          hint: mostRecentImport ? (
            <span className="flex flex-col leading-snug">
              <span>{fmtRelative(mostRecentImport.created_at)}</span>
              <span className="truncate text-slate-500" title={`${mostRecentImport.organization?.organization_name || 'Unknown org'} · ${mostRecentImport.file_name || ''}`}>
                {mostRecentImport.organization?.organization_name || 'Unknown org'}
                {mostRecentImport.file_name ? ` · ${mostRecentImport.file_name}` : ''}
              </span>
            </span>
          ) : 'Upload your first file to get started',
          testId: 'model-setup-stat-last',
        },
        {
          key: 'templates',
          label: 'Saved Templates',
          value: importTemplates.length,
          hint: importTemplates.length > 0
            ? 'Re-apply column mappings instantly'
            : 'Save a template after import',
          testId: 'model-setup-stat-templates',
        },
      ]}
    />
  );

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Model Setup"
        description="Configure your ROI model inputs and upload baseline data. Define organization context, target improvements, and import the LEI, KPI, and program datasets that feed every downstream calculation."
        icon={SlidersHorizontal}
        theme={__pageTheme}
        stats={heroStats}
        testId="model-setup-hero"
      />
    )}>
    <div className="space-y-6 animate-fade-in">
      {/* Header - matching standard style */}
      
      {/* Purpose Clarification */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-900 font-medium">When to use this page:</p>
              <p className="text-sm text-blue-700 mt-1">
                Use <strong>Model Configuration Import</strong> to set up your ROI calculations with organization details and target improvements. 
                This defines <em>where you are now</em> and <em>where you want to be</em>.
              </p>
              <p className="text-sm text-blue-600 mt-2">
                Looking to track actual progress over time? Use{' '}
                <button onClick={() => navigate('/data-management')} className="text-blue-700 underline font-medium hover:text-blue-900">
                  Data Management
                </button>{' '}
                to import ongoing LEI measurements and KPI actuals.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="advanced" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Advanced Import
          </TabsTrigger>
          <TabsTrigger value="simple" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Simple Import
          </TabsTrigger>
        </TabsList>

        {/* Advanced Import Tab */}
        <TabsContent value="advanced" className="space-y-6 mt-6">
          {/* What's Included */}
          <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Database className="h-5 w-5" />
                What This Template Includes
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-slate-800">Organization Setup</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Workforce size, revenue, L&D budget, industry type, execution factors
                  </p>
                  <p className="text-xs text-purple-600 mt-2 font-medium">→ Used for ROI calculations</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-teal-600" />
                    <span className="font-medium text-slate-800">Current LEI Baselines</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Your starting point for each LEI measure (participation rates, scores, etc.)
                  </p>
                  <p className="text-xs text-teal-600 mt-2 font-medium">→ Defines "where you are now"</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-slate-800">Target LEI Values</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Your improvement goals for modeling projected ROI impact
                  </p>
                  <p className="text-xs text-amber-600 mt-2 font-medium">→ Defines "where you want to be"</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Download Advanced Template */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-purple-600" />
                Advanced Import Template
              </CardTitle>
              <CardDescription>
                Download the comprehensive template with all LEI measure variables
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div>
                  <p className="font-medium text-slate-900">LEI Advanced Import Template</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Includes: Organization params, Current LEI values, Target LEI values, KPI selection
                  </p>
                </div>
                <Button
                  onClick={handleDownloadAdvancedTemplate}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="download-advanced-template-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Template
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upload Advanced File */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="h-5 w-5 text-navy-700" />
                Import Advanced Data
              </CardTitle>
              <CardDescription>
                Upload your completed advanced template with LEI measure data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors cursor-pointer bg-purple-50/30"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, true)}
                onClick={() => advancedFileInputRef.current?.click()}
                data-testid="advanced-file-drop-zone"
              >
                <input
                  ref={advancedFileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleAdvancedFileSelect}
                  className="hidden"
                  data-testid="advanced-file-input"
                />
                
                {isUploading ? (
                  <div className="flex flex-col items-center">
                    <div className="spinner mb-4" />
                    <p className="text-slate-600">Processing advanced data...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-900">
                      Drop your advanced template here
                    </p>
                    <p className="text-slate-500 mt-2">
                      or click to browse
                    </p>
                    <p className="text-xs text-slate-400 mt-4">
                      Supports .xlsx files only
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Advanced Import Results */}
          {advancedImportData && uploadResult?.type === 'advanced' && (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="h-5 w-5" />
                  Import Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Organization Summary */}
                <div className="bg-white rounded-lg p-4 border">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    Organization
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Name:</span>
                      <p className="font-medium">{advancedImportData.organization?.organization_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Workforce:</span>
                      <p className="font-medium">{advancedImportData.organization?.total_workforce?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Industry:</span>
                      <p className="font-medium capitalize">{advancedImportData.organization?.industry_type}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Budget:</span>
                      <p className="font-medium">{formatCurrency(advancedImportData.organization?.intervention_budget, currency)}</p>
                    </div>
                  </div>
                </div>

                {/* LEI Data Summary */}
                <div className="bg-white rounded-lg p-4 border">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-teal-500" />
                    LEI Measures Imported
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-teal-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-teal-700">
                        {advancedImportData.summary?.measures_with_current_data || 0}
                      </p>
                      <p className="text-xs text-teal-600">Current Values</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-amber-700">
                        {advancedImportData.summary?.measures_with_target_data || 0}
                      </p>
                      <p className="text-xs text-amber-600">Target Values</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-purple-700">
                        {advancedImportData.summary?.selected_kpis_count || 0}
                      </p>
                      <p className="text-xs text-purple-600">KPIs Selected</p>
                    </div>
                  </div>
                </div>

                {/* LEI Improvements Preview */}
                {Object.keys(advancedImportData.lei_improvements || {}).length > 0 && (
                  <div className="bg-white rounded-lg p-4 border">
                    <h4 className="font-medium text-slate-800 mb-3">Calculated Improvements</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {Object.entries(advancedImportData.lei_improvements).map(([measureId, data]) => (
                        <div key={measureId} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                          <span className="text-slate-600 capitalize">{measureId.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-slate-500">
                              {data.current_score?.toFixed(1)}% → {data.target_score?.toFixed(1)}%
                            </span>
                            <Badge variant={data.improvement > 0 ? 'default' : 'secondary'} className="min-w-[60px] justify-center">
                              {data.improvement > 0 ? '+' : ''}{data.improvement?.toFixed(1)}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analysis Results Summary (if analysis was run) */}
                {advancedImportData.analysis_results && (
                  <div className="bg-white rounded-lg p-4 border border-teal-200">
                    <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-teal-600" />
                      Analysis Results
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-3 text-center">
                        <p className="text-xs text-teal-600 mb-1">Total Financial Impact</p>
                        <p className="text-lg font-bold text-teal-700">
                          {formatCurrency(advancedImportData.analysis_results.total_financial_impact, currency)}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 text-center">
                        <p className="text-xs text-emerald-600 mb-1">Net Benefit</p>
                        <p className="text-lg font-bold text-emerald-700">
                          {formatCurrency(advancedImportData.analysis_results.total_net_benefit, currency)}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 text-center">
                        <p className="text-xs text-blue-600 mb-1">ROI</p>
                        <p className="text-lg font-bold text-blue-700">
                          {advancedImportData.analysis_results.total_roi_percent?.toFixed(0)}%
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 text-center">
                        <p className="text-xs text-purple-600 mb-1">P(ROI &gt; 0)</p>
                        <p className="text-lg font-bold text-purple-700">
                          {advancedImportData.analysis_results.prob_positive_roi?.toFixed(0)}%
                        </p>
                      </div>
                    </div>
                    
                    {/* Monte Carlo Summary */}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2">Monte Carlo Simulation (5,000 iterations)</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">ROI Range:</span>
                        <span className="font-medium">
                          {advancedImportData.analysis_results.roi_p5?.toFixed(0)}% 
                          <span className="text-slate-400 mx-1">—</span> 
                          {advancedImportData.analysis_results.roi_p95?.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Created Scenario Info */}
                {createdScenario && (
                  <div className="bg-gradient-to-r from-emerald-50 to-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-emerald-800 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Scenario Created Successfully
                        </h4>
                        <p className="text-sm text-emerald-700 mt-1">{createdScenario.name}</p>
                        <p className="text-xs text-emerald-600 mt-1">
                          Share Token: <code className="bg-white/50 px-1 rounded">{createdScenario.share_token}</code>
                        </p>
                      </div>
                      <Button
                        onClick={() => navigate('/scenarios')}
                        variant="outline"
                        size="sm"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        data-testid="view-all-scenarios-btn"
                      >
                        View All Scenarios
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between items-center gap-3 pt-2">
                  <Button
                    onClick={() => setShowSaveTemplateDialog(true)}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="save-as-template-btn"
                  >
                    <Bookmark className="mr-2 h-4 w-4" />
                    Save as Template
                  </Button>
                  <div className="flex gap-3">
                    {createdScenario && (
                      <Button
                        onClick={() => navigate('/scenarios')}
                        variant="outline"
                        data-testid="view-scenarios-btn"
                      >
                        View Saved Scenarios
                      </Button>
                    )}
                    <Button
                      onClick={proceedToAnalysis}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="proceed-to-analysis-btn"
                    >
                      {createdScenario ? 'View Full Results' : 'Proceed to Analysis'}
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Simple Import Tab */}
        <TabsContent value="simple" className="space-y-6 mt-6">
          {/* Download Template */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-teal-600" />
                Simple Configuration Template
              </CardTitle>
              <CardDescription>
                Download a basic template for single-measure analysis configuration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">LEI Analysis Template</p>
                  <p className="text-sm text-slate-500">
                    Includes LEI measures, KPIs, and parameter configuration
                  </p>
                </div>
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  data-testid="download-template-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Template
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="h-5 w-5 text-navy-700" />
                Import Configuration
              </CardTitle>
              <CardDescription>
                Upload a completed Excel file to import your analysis configuration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-navy-500 transition-colors cursor-pointer"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, false)}
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                
                {isUploading ? (
                  <div className="flex flex-col items-center">
                    <div className="spinner mb-4" />
                    <p className="text-slate-600">Processing file...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-900">
                      Drop your Excel file here
                    </p>
                    <p className="text-slate-500 mt-2">
                      or click to browse
                    </p>
                    <p className="text-xs text-slate-400 mt-4">
                      Supports .xlsx files only
                    </p>
                  </>
                )}
              </div>

              {/* Upload Result */}
              {uploadResult && uploadResult.type !== 'advanced' && (
                <div
                  className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
                    uploadResult.success
                      ? 'bg-emerald-50 border border-emerald-200'
                      : 'bg-rose-50 border border-rose-200'
                  }`}
                >
                  {uploadResult.success ? (
                    <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p
                      className={`font-medium ${
                        uploadResult.success ? 'text-emerald-800' : 'text-rose-800'
                      }`}
                    >
                      {uploadResult.message}
                    </p>
                    {uploadResult.success && uploadResult.data && (
                      <div className="mt-2 text-sm text-emerald-700">
                        <p>• LEI Measure: {uploadResult.data.lei_measure_id}</p>
                        <p>• KPI: {uploadResult.data.kpi_id}</p>
                        <p>• Improvement: {uploadResult.data.lei_improvement}%</p>
                        <p>• Cost: {formatCurrency(uploadResult.data.intervention_cost, currency)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Next Steps */}
          {uploadResult?.success && uploadResult?.type !== 'advanced' && (
            <Card className="border-teal-200 bg-teal-50/30">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-teal-900">Configuration imported successfully!</p>
                    <p className="text-sm text-teal-700 mt-1">
                      Ready to run your analysis with the imported settings
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/configure')}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="proceed-to-config-btn"
                  >
                    Review & Run Analysis
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Import History & Templates Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Import History */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-600" />
              Recent Imports
            </CardTitle>
            <CardDescription>Last 10 imports with quick re-run option</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
              </div>
            ) : importHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No import history yet</p>
                <p className="text-sm">Import a file to see it here</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[320px] overflow-y-auto">
                {importHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`history-entry-${entry.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate text-sm">
                        {entry.organization?.organization_name || 'Unknown'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {entry.file_name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {entry.summary?.measures_with_current_data || 0} measures
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                        {entry.summary?.is_rerun && (
                          <Badge variant="secondary" className="text-xs">Re-run</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {entry.scenario_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate('/scenarios')}
                          className="text-slate-600 hover:text-teal-600"
                          title="View Scenario"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRerunImport(entry.id)}
                        disabled={isRerunning === entry.id}
                        className="text-slate-600 hover:text-teal-600"
                        title="Re-run Analysis"
                        data-testid={`rerun-btn-${entry.id}`}
                      >
                        {isRerunning === entry.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteHistoryEntry(entry.id)}
                        className="text-slate-400 hover:text-rose-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saved Templates */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-amber-600" />
              Saved Templates
            </CardTitle>
            <CardDescription>Pre-configured import templates for recurring analyses</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
              </div>
            ) : importTemplates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bookmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No templates saved yet</p>
                <p className="text-sm">Import data and save as template</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[320px] overflow-y-auto">
                {importTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg hover:bg-amber-50 transition-colors border border-amber-100"
                    data-testid={`template-entry-${template.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate text-sm">
                        {template.name}
                      </p>
                      {template.description && (
                        <p className="text-xs text-slate-500 truncate">{template.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-white">
                          {template.organization?.organization_name || 'Custom'}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          Used {template.use_count || 0}x
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadTemplateFile(template.id, template.name)}
                        className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
                        title="Download Pre-filled Template"
                        data-testid={`download-template-btn-${template.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-slate-400 hover:text-rose-600"
                        title="Delete Template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Save as Template Dialog */}
      {showSaveTemplateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="save-template-dialog">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Save className="h-5 w-5 text-amber-600" />
                Save as Template
              </CardTitle>
              <CardDescription>
                Save this import configuration for future use
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Template Name *</label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Q1 2026 Training Analysis"
                  className="mt-1"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description (optional)</label>
                <Input
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="e.g., Annual workforce training impact"
                  className="mt-1"
                  data-testid="template-description-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSaveTemplateDialog(false);
                    setTemplateName('');
                    setTemplateDescription('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAsTemplate}
                  disabled={!templateName.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="save-template-confirm-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Section */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-slate-400 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-2">Import Tips</p>
              <ul className="list-disc list-inside space-y-1 text-slate-500">
                <li><strong>Advanced Import:</strong> Use for full LEI modeling with raw data inputs (recommended)</li>
                <li><strong>Simple Import:</strong> Use for quick single-measure configuration</li>
                <li>Download the template first and follow the format exactly</li>
                <li>LEI scores are auto-calculated from raw variable inputs</li>
                <li>Leave cells blank to use system defaults</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageShell>
  );
};

export default ImportPage;
